import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import { Toaster } from 'react-hot-toast';
import { Context } from './context/Context';
import { CouponsContext } from './context/Coupons';
import AuditTracker from './components/AuditTracker';
import { safeReadPermissionsFromStorage } from './utils/permissions';
import OfficeAccessGuard from './components/OfficeAccessGuard';

const lazyWithPreload = (factory) => {
  const Component = lazy(factory);
  Component.preload = factory;
  return Component;
};

const Dashboard = lazyWithPreload(() => import('./pages/Dashboard'));
const Login = lazyWithPreload(() => import('./pages/Login'));
const Signup = lazyWithPreload(() => import('./pages/Signup'));
const Orders = lazyWithPreload(() => import('./pages/Orders'));
const Invoices = lazyWithPreload(() => import('./pages/Invoices'));
const Riders = lazyWithPreload(() => import('./pages/Riders'));
const PriceManipulation = lazyWithPreload(() => import('./pages/PriceManipulation'));
const ApproveARider = lazyWithPreload(() => import('./pages/ApproveARider'));
const Transaction = lazyWithPreload(() => import('./pages/Transaction'));
const Pay = lazyWithPreload(() => import('./pages/Pay'));
const CouponsAndOffer = lazyWithPreload(() => import('./pages/CouponsAndOffer'));
const Layouttest = lazyWithPreload(() => import('./components/Layouttest'));
const Home = lazyWithPreload(() => import('./pages/Home'));
const ActiveRider = lazyWithPreload(() => import('./pages/RidersActive'));
const AddCity = lazyWithPreload(() => import('./pages/AddCity'));
const Order = lazyWithPreload(() => import('./pages/Order'));
const DashboardChart = lazyWithPreload(() => import('./pages/DashboardChart'));
const AdminChatPage = lazyWithPreload(() => import('./pages/SupportChatPage'));
const Customers = lazyWithPreload(() => import('./pages/Customers'));
const Contacts = lazyWithPreload(() => import('./pages/Contacts'));
const RiderDetails = lazyWithPreload(() => import('./pages/RiderDetails'));
const RiderTransactions = lazyWithPreload(() => import('./pages/RiderTransactions'));
const RiderTransactionDetails = lazyWithPreload(() => import('./pages/RiderTransactionDetails'));
const CustomerDetails = lazyWithPreload(() => import('./pages/CustomerDetails'));
const CustomerTransactions = lazyWithPreload(() => import('./pages/CustomerTransactions'));
const CustomerTransactionDetails = lazyWithPreload(() => import('./pages/CustomerTransactionDetails'));
const Admins = lazyWithPreload(() => import('./pages/Admins'));
const ApproveRiderDetails = lazyWithPreload(() => import('./pages/ApproveRiderDetails'));
const RiderQuickReview = lazyWithPreload(() => import('./pages/RiderQuickReview'));
const DashboardNew = lazyWithPreload(() => import('./pages/DashboardNew'));
const Notifications = lazyWithPreload(() => import('./pages/Notifications'));
const ApiPartners = lazyWithPreload(() => import('./pages/ApiPartners'));
const ApiPartnerDetails = lazyWithPreload(() => import('./pages/ApiPartnerDetails'));
const Logging = lazyWithPreload(() => import('./pages/Logging'));
const MasterAnalytics = lazyWithPreload(() => import('./pages/MasterAnalytics'));
const CRMSettings = lazyWithPreload(() => import('./pages/CRMSettings'));
const ReferralManagement = lazyWithPreload(() => import('./pages/ReferralManagement'));
const AddRider = lazyWithPreload(() => import('./pages/AddRider'));
const AddedRiders = lazyWithPreload(() => import('./pages/AddedRiders'));
const DeleteRider = lazyWithPreload(() => import('./pages/DeleteRider'));
const Blogs = lazyWithPreload(() => import('./pages/Blogs'));
const BlogEditor = lazyWithPreload(() => import('./pages/BlogEditor'));
const BlogAnalytics = lazyWithPreload(() => import('./pages/BlogAnalytics'));

const protectedRouteModules = [
  Dashboard,
  DashboardNew,
  DashboardChart,
  Orders,
  Invoices,
  Order,
  Riders,
  ActiveRider,
  RiderDetails,
  RiderTransactions,
  RiderTransactionDetails,
  PriceManipulation,
  ApproveARider,
  ApproveRiderDetails,
  AddRider,
  AddedRiders,
  DeleteRider,
  Transaction,
  Customers,
  CustomerDetails,
  CustomerTransactions,
  CustomerTransactionDetails,
  Contacts,
  CouponsAndOffer,
  AddCity,
  AdminChatPage,
  Notifications,
  CRMSettings,
  ReferralManagement,
  Logging,
  Admins,
  ApiPartners,
  ApiPartnerDetails,
  MasterAnalytics,
];

const RouteLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-[#fffdf8]">
    <div className="flex flex-col items-center gap-3">
      <div className="h-12 w-12 animate-spin rounded-full border-4 border-yellow-200 border-t-yellow-500" />
      <p className="text-sm font-semibold text-slate-600">Loading page...</p>
    </div>
  </div>
);

const DashboardChartWithContext = () => (
  <Context>
    <DashboardChart />
  </Context>
);

const CouponsAndOfferWithContext = () => (
  <CouponsContext>
    <CouponsAndOffer />
  </CouponsContext>
);

function App() {
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    let cancelled = false;
    let timerId = null;

    const preloadBatch = (startIndex = 0) => {
      if (cancelled || startIndex >= protectedRouteModules.length) {
        return;
      }

      protectedRouteModules
        .slice(startIndex, startIndex + 4)
        .forEach((RouteComponent) => {
          RouteComponent.preload?.().catch(() => {});
        });

      timerId = window.setTimeout(() => preloadBatch(startIndex + 4), 120);
    };

    const startPreloading = () => {
      timerId = window.setTimeout(() => preloadBatch(0), 250);
    };

    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(startPreloading, { timeout: 1200 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
        if (timerId) window.clearTimeout(timerId);
      };
    }

    startPreloading();
    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, []);

  const ProtectedRoute = ({ children, requiredPermission }) => {
    const token = localStorage.getItem('token');
    if (!token) return <Navigate to="/" replace />;

    const permissions = safeReadPermissionsFromStorage();
    if (!permissions) {
      return <Navigate to="/" replace />;
    }

    if (requiredPermission && !permissions[requiredPermission]) {
      return <Navigate to="/unauthorized" replace />;
    }

    return <OfficeAccessGuard>{children}</OfficeAccessGuard>;
  };

  const UnauthorizedPage = () => {
    const navigate = useNavigate();

    return (
      <div className="bg-gradient-to-br from-blue-50 to-gray-100 min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden w-full max-w-md">
          <div className="bg-red-500 p-4">
            <h1 className="text-2xl font-bold text-white text-center">
              ⚠️ Access Restricted
            </h1>
          </div>

          <div className="p-8 text-center">
            <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>

            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Permission Denied
            </h2>
            <p className="text-gray-600 mb-6">
              Your account doesn't have access to this page.
            </p>

            <button
              onClick={() => navigate(-1)}
              className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              ← Go Back
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Router>
          <AuditTracker />
          <Suspense fallback={<RouteLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Login />} />
                <Route path="/home" element={<Home />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                <Route path="/layout" element={<Layouttest />} />

                {/* Dashboard routes */}
                <Route path="/invoices" element={<ProtectedRoute requiredPermission="invoiceSystem"><Invoices /></ProtectedRoute>} />
                <Route path="/blogs" element={<ProtectedRoute requiredPermission="crm"><Blogs /></ProtectedRoute>} />
                <Route path="/blogs/:id/analytics" element={<ProtectedRoute requiredPermission="crm"><BlogAnalytics /></ProtectedRoute>} />
                <Route path="/blogs/new" element={<ProtectedRoute requiredPermission="crm"><BlogEditor /></ProtectedRoute>} />
                <Route path="/blogs/:id/edit" element={<ProtectedRoute requiredPermission="crm"><BlogEditor /></ProtectedRoute>} />
                <Route path="/pay" element={<ProtectedRoute requiredPermission="moneyController"><Pay /></ProtectedRoute>} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute requiredPermission="dashboard">
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboardNew"
                  element={
                    <ProtectedRoute requiredPermission="dashboardChart">
                      <DashboardNew />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/chart"
                  element={
                    <ProtectedRoute requiredPermission="dashboardChart">
                      <DashboardChartWithContext />
                    </ProtectedRoute>
                  }
                />

                {/* Order routes */}
              <Route
                path="/master-analytics"
                element={
                  <ProtectedRoute requiredPermission="masterAnalytics">
                    <MasterAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                  path="/orders"
                  element={
                    <ProtectedRoute requiredPermission="orders">
                      <Orders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/order/:id"
                  element={
                    <ProtectedRoute requiredPermission="orders">
                      <Order />
                    </ProtectedRoute>
                  }
                />

                {/* Rider routes */}
                <Route
                  path="/riders"
                  element={
                    <ProtectedRoute requiredPermission="riders">
                      <Riders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/riders/active"
                  element={
                    <ProtectedRoute requiredPermission="riders">
                      <ActiveRider />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/riders/delete"
                  element={
                    <ProtectedRoute requiredPermission="riders">
                      <DeleteRider />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/riders/:id"
                  element={
                    <ProtectedRoute requiredPermission="riders">
                      <RiderDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/riders/transactions"
                  element={
                    <ProtectedRoute requiredPermission="riders">
                      <RiderTransactions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/riders/transactions/:id"
                  element={
                    <ProtectedRoute requiredPermission="riders">
                      <RiderTransactionDetails />
                    </ProtectedRoute>
                  }
                />

                {/* Price manipulation */}
                <Route
                  path="/price-manipulation"
                  element={
                    <ProtectedRoute requiredPermission="priceManipulation">
                      <PriceManipulation />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/approved"
                  element={
                    <ProtectedRoute requiredPermission="approveARider">
                      <ApproveARider />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pending/details/:id"
                  element={
                    <ProtectedRoute requiredPermission="approveARider">
                      <ApproveRiderDetails />
                    </ProtectedRoute>
                  }
                />
                <Route path="/approve-riders/quick-review" element={
                  <ProtectedRoute requiredPermission="approveARider"><RiderQuickReview /></ProtectedRoute>
                } />
                <Route
                  path="/incomplete"
                  element={
                    <ProtectedRoute requiredPermission="approveARider">
                      <ApproveARider />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/other-state-city"
                  element={
                    <ProtectedRoute requiredPermission="approveARider">
                      <ApproveARider />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/not-interested"
                  element={
                    <ProtectedRoute requiredPermission="approveARider">
                      <ApproveARider />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/rejected"
                  element={
                    <ProtectedRoute requiredPermission="approveARider">
                      <ApproveARider />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pending"
                  element={
                    <ProtectedRoute requiredPermission="approveARider">
                      <ApproveARider />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/approve-riders/add"
                  element={
                    <ProtectedRoute requiredPermission="approveARider">
                      <AddRider />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/approve-riders/added"
                  element={
                    <ProtectedRoute requiredPermission="approveARider">
                      <AddedRiders />
                    </ProtectedRoute>
                  }
                />

                {/* Transaction routes */}
                <Route
                  path="/transaction"
                  element={
                    <ProtectedRoute requiredPermission="transaction">
                      <Transaction />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/transaction/:id"
                  element={
                    <ProtectedRoute requiredPermission="transaction">
                      <Transaction />
                    </ProtectedRoute>
                  }
                />

              {/* Coupons and offers */}
              <Route
                path="/coupons-and-offers"
                element={
                  <ProtectedRoute requiredPermission="couponsAndOffers">
                    <CouponsAndOfferWithContext />
                  </ProtectedRoute>
                }
              />

              {/* City management */}
              <Route
                path="/add-city"
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <AddCity />
                  </ProtectedRoute>
                }
              />

              {/* Chat */}
              <Route
                path="/admin/chat"
                element={
                  <ProtectedRoute requiredPermission="chats">
                    <AdminChatPage />
                  </ProtectedRoute>
                }
              />

              {/* Notifications */}
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute requiredPermission="notifications">
                    <Notifications />
                  </ProtectedRoute>
                }
              />

              {/* Customer routes */}
              <Route
                path="/customers"
                element={
                  <ProtectedRoute requiredPermission="customers">
                    <Customers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/:id"
                element={
                  <ProtectedRoute requiredPermission="customers">
                    <CustomerDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/transactions"
                element={
                  <ProtectedRoute requiredPermission="customers">
                    <CustomerTransactions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/transactions/:id"
                element={
                  <ProtectedRoute requiredPermission="customers">
                    <CustomerTransactionDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/contact"
                element={
                  <ProtectedRoute requiredPermission="contact">
                    <Contacts />
                  </ProtectedRoute>
                }
              />

              {/* Admin management */}
              <Route
                path="/crm-settings"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/referrals"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <ReferralManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm-settings/rider-order-alerts"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm-settings/serviceable-area"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm-settings/payments"
                element={<ProtectedRoute requiredPermission="crm"><CRMSettings /></ProtectedRoute>}
              />
              <Route
                path="/crm-settings/heatmap"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm-settings/new-order-text"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm-settings/customer-updates"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm-settings/customer-updates/:updateId"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm-settings/update-notification"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm-settings/faqs"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm-settings/support-chat"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm-settings/stories"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm-settings/refer-and-earn-customer"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm-settings/refer-and-earn-rider"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm-settings/joining-bonus-riders"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm-settings/coupons-for-customers"
                element={
                  <ProtectedRoute requiredPermission="crm">
                    <CRMSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/logging"
                element={
                  <ProtectedRoute requiredPermission="logging">
                    <Logging />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admins"
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <Admins />
                  </ProtectedRoute>
                }
              />

                <Route
                  path="/signup"
                  element={
                    <ProtectedRoute requiredPermission="signup">
                      <Signup />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/api-partners"
                  element={
                    <ProtectedRoute requiredPermission="admin">
                      <ApiPartners />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/api-partners/:id"
                  element={
                    <ProtectedRoute requiredPermission="admin">
                      <ApiPartnerDetails />
                    </ProtectedRoute>
                  }
                />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 3000,
                success: { duration: 3000 },
                error: { duration: 4000 },
              }}
            />
          </Suspense>
    </Router>
  );
}

export default App;
